/**
 * Resolves widget options from the two ways a merchant can configure the
 * snippet: `window.SuperChat = { ... }` (what the server's install-snippet
 * emits) and `data-*` attributes on the script tag itself.
 *
 * Script attributes win, because they sit closest to the embed.
 */

const BOOL_TRUE = new Set(['', 'on', 'true', '1', 'yes']);

function currentScript() {
  if (document.currentScript) return document.currentScript;
  // `async` scripts lose document.currentScript by the time we run.
  const tags = document.querySelectorAll('script[src*="superchat"]');
  return tags[tags.length - 1] ?? null;
}

/** Local development, by hostname — never true on a real merchant domain. */
function isLocalHost() {
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.localhost')
  );
}

function originOf(url) {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
}

/**
 * The side of the screen, as one of exactly two values.
 *
 * Anything unrecognised falls back to the right rather than throwing: the
 * widget runs on pages whose snippet we do not control, and a typo in an
 * appearance option must not cost the merchant their chat.
 */
export function normalizePosition(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .endsWith('left')
    ? 'left'
    : 'right';
}

export function resolveConfig(overrides = {}) {
  const script = currentScript();
  const data = script?.dataset ?? {};
  const global = window.SuperChat ?? {};

  // Fall back to the origin the bundle itself was served from — that is the
  // API host in every deployment where Express serves /widget.
  const scriptOrigin = script?.src ? originOf(script.src) : null;

  const config = {
    publicKey: data.appKey ?? global.publicKey ?? overrides.publicKey ?? null,
    apiBase: (data.apiBase ?? global.apiBase ?? overrides.apiBase ?? scriptOrigin ?? '').replace(
      /\/+$/,
      ''
    ),
    // The host app knows which store this is; it prefills the pre-chat form.
    shop: data.shop ?? global.shop ?? overrides.shop ?? '',
    brand: data.brand ?? global.brand ?? overrides.brand ?? '',
    accent: data.accent ?? global.accent ?? overrides.accent ?? null,
    // Which side of the screen. 'left' and 'right'; the older 'bottom-left' /
    // 'bottom-right' spellings still work, so deployed snippets keep running.
    position: normalizePosition(data.position ?? global.position ?? overrides.position),
    offset: Number(data.offset ?? global.offset ?? overrides.offset ?? 24),
    theme: data.theme ?? global.theme ?? overrides.theme ?? 'auto',
    // White-label look: the header fills with the brand accent instead of
    // sitting on the panel background.
    solidHeader: BOOL_TRUE.has(String(data.solidHeader ?? global.solidHeader ?? 'false')),
    /* `emergencySound` used to live here, switching a distinct alarm tone on
       an agent alert. There is no distinct tone any more — an alert force-opens
       the panel and chimes like any other message — so the option is gone
       rather than kept as a switch that does nothing. Snippets still passing it
       are simply ignored. */
    showBadge: !BOOL_TRUE.has(String(data.hideBadge ?? global.hideBadge ?? 'false')),
    // Web Push needs a service worker on the *host* origin; the merchant opts
    // in by pointing us at one they serve themselves.
    serviceWorkerPath: data.serviceWorker ?? global.serviceWorkerPath ?? null,
    // The exact top-level URL, when the host page can supply it. Inside an
    // embedded Shopify app the browser will not reveal the admin URL to us —
    // the host app is the only party that knows it.
    topUrl: data.topUrl ?? global.topUrl ?? overrides.topUrl ?? null,
    visitor: global.visitor ?? overrides.visitor ?? {},
    // Staffed hours, so "we're offline" can name the next staffed hour rather
    // than shrugging. Shape: { tz, days: { 1: [9, 18], ... } }
    hours: global.hours ?? overrides.hours ?? null,
    debug: BOOL_TRUE.has(String(data.debug ?? global.debug ?? 'false')),
    // Diagnostics row inside the panel. On automatically for local development
    // — where you always want it — and off everywhere else, so it can never
    // appear in a real merchant's admin. Set explicitly to override either way.
    testTools: BOOL_TRUE.has(
      String(data.testTools ?? global.testTools ?? (isLocalHost() ? 'true' : 'false'))
    ),
  };

  if (!config.publicKey) {
    throw new Error('SuperChat: no publicKey — set data-app-key on the script tag');
  }
  if (!config.apiBase) {
    throw new Error('SuperChat: no apiBase — set data-api-base on the script tag');
  }

  return config;
}

/**
 * Storage that cannot take the widget down with it.
 *
 * The widget runs in a third-party iframe — inside a Shopify admin, that is
 * exactly the context browsers restrict. Safari's ITP and Firefox's strict
 * mode make `localStorage` *throw* rather than return null, so an unguarded
 * read during boot would fail the whole widget instead of costing us one
 * remembered id.
 *
 * Falls back to memory: the visitor keeps one conversation for the life of the
 * page, and starts a fresh one on reload. Degraded, but working.
 */
const memoryStore = new Map();

export const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return memoryStore.get(key) ?? null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      memoryStore.set(key, value);
    }
  },
  /** True when values survive a reload — surfaced in diagnostics. */
  get persistent() {
    try {
      const probe = '__sc_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  },
};

/** Storage keys are namespaced per app so two widgets never collide. */
export function storageKeys(publicKey) {
  const ns = `superchat:${publicKey.slice(0, 16)}`;
  return {
    visitorId: `${ns}:visitorId`,
    muted: `${ns}:muted`,
    seenAt: `${ns}:seenAt`,
    // "I dismissed the email prompt." Remembered so the full card is asked
    // once and never again, while the one-line version stays available.
    emailDismissed: `${ns}:emailDismissed`,
  };
}

export default { resolveConfig, normalizePosition, storageKeys };
